<template>
    <div class="content">
        <h2 class="h2">{{ t('title') }}</h2>
        <div
            v-click-outside="{ handler: () => showDatePicker = false }"
            class="dateinput"
        >
            <DSInput
                v-model="order.data.value.date"
                type="text"
                :label="t('date')"
                :help-text="t('required')"
                readonly
                @keydown.enter="showDatePicker = !showDatePicker"
                @click="showDatePicker = !showDatePicker"
            />
            <DSCalendar
                v-if="showDatePicker"
                v-model="calendarDate"
                :min-date="DateTime.now().plus({ days: 2 })"
                @update:model-value="selectDate"
            />
        </div>
        <DSCombobox
            v-model="order.data.value.time"
            class="time"
            :label="t('time')"
            :options="timeOptions"
            :help-text="t('required')"
        />
        <DSInput
            v-model="order.data.value.persons"
            type="number"
            :label="t('amountOfPeople')"
            :help-text="t('amountOfPeopleText')"
            :error="order.errors.value.persons"
        />
        <template v-if="station">
            <p style="margin-bottom: 0;">
                <strong>{{ t('onlyByCallingHeading') }}</strong><br />
                <button class="station-info" @click="stationInfoOpen = !stationInfoOpen">{{ t('stationInfo') }}</button>
            </p>
            <DSHeightTransition :open="stationInfoOpen">
                <a v-if="station.telephone" :href="`tel:${station.telephone}`" class="ds-btn ds-btn--large ds-btn--primary phone-link">{{ t('callStation') }}</a>
                <p>
                    <strong>{{ station.name }}</strong><br />
                    {{ station.street }}<br v-if="station.street" />
                    {{ station.postcode }} <span class="city">{{ station.city?.toLowerCase() }}</span>
                </p>
                <p v-if="station.telephone">
                    {{ t('phoneNumber') }}<br />
                    <strong>{{ station.telephone }}</strong>
                </p>
                <p v-if="station.openingHoursTable?.station">
                    {{ t('stationOpeningHours') }}<br />
                    <BlocksOpeningHours
                        :opening-hours="station.openingHoursTable?.station"
                    />
                </p>
            </DSHeightTransition>
        </template>
    </div>
</template>
<script setup>
import { ref, computed, inject, useI18n } from '#imports';
import { DateTime } from 'luxon';
import vClickOutside from '~dsc/directives/click-outside';

const stationInfoOpen = ref(false);
const order = inject('order');

defineOptions({
    inheritAttrs: false,
});

defineProps({
    station: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const timeOptions = computed(() => {
    const startTime = 0;
    const endTime = 24;
    const times = [];

    for (let index = startTime; index < endTime; index++) {
        times.push(
            { value: `${index}:00`, label: `${index}:00` },
            { value: `${index}:15`, label: `${index}:15` },
            { value: `${index}:30`, label: `${index}:30` },
            { value: `${index}:45`, label: `${index}:45` },
        );
    }

    return times;
});

const showDatePicker = ref(false);
const calendarDate = ref(DateTime.now().plus({ days: 3 }).set({ hours: 0, minutes: 0 }));

function selectDate(date) {
    const formattedDate = date.toFormat('d.M.yyyy');
    order.data.value.date = formattedDate;
    showDatePicker.value = false;
}

const { t } = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            title: 'Valitse aika ja henkilömäärä',
            date: 'Päivämäärä',
            time: 'Ruokailuaika',
            amountOfPeople: 'Henkilömäärä',
            amountOfPeopleText: 'Pakollinen, vähintään 8',
            onlyByCallingHeading: 'Tarjouspyyntö samalle tai seuraavalle 2 päivälle vain soittamalla',
            callStation: 'Soita asemalle',
            phoneNumber: 'Puhelinnumero',
            stationOpeningHours: 'Aseman aukioloajat',
            stationInfo: 'Asematiedot',
        },
        'en-EU': {
            title: 'Valitse aika, milloin haluaisitte ruokailla?',
            date: 'Päivämäärä',
            time: 'Ruokailuaika',
            amountOfPeople: 'Henkilömäärä',
            amountOfPeopleText: 'Pakollinen, vähintään 8',
            onlyByCallingHeading: 'Tarjouspyyntö samalle tai seuraavalle 2 päivälle vain soittamalla',
            callStation: 'Soita asemalle',
            phoneNumber: 'Puhelinnumero',
            stationOpeningHours: 'Aseman aukioloajat',
            stationInfo: 'Asematiedot',
        },
    },
});
</script>
<style scoped>
.h2 {
  margin-bottom: 0;
}

.content {
  display: flex;
  flex-flow: column wrap;
  gap: 1rem;
}

.ds-calendar {
  position: absolute;
  z-index: 2;
}

.ds-input {
  margin-bottom: 0;
}
.ds-input :deep(input) {
  opacity: 1;
}

.station-info {
  color: var(--color-primary-500);
  font-weight: 700;
  text-decoration: underline;
}

.city {
  text-transform: capitalize;
}

.message {
  font-size: 0.75rem;
  color: var(--color-base-grey-600);
  margin-bottom: 1rem;
}

.open-hours {
  max-width: 240px;
}

.phone-link {
  margin-bottom: 1rem;
}
</style>
